/* © CNES */
import { useQueries, useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeycloakProfile } from 'keycloak-js';
import { keycloakAdapter } from '../../../api/authentication/AuthenticateAPI';
import { EditProfile } from '../../../api/editProfile/EditProfile';
import {
  getDownloadQuotas,
  getQuotasProcess,
} from '../../../api/process/process';
import { ConfigContext } from '../../../context/ConfigContext';
import { KeycloakUserInfo } from '../../../models/KeycloakUserInfo';
import {
  MyGeneralInformation,
  toMyKeycloakProfile,
  MyProfileInformation,
} from '../../../models/MyInformation';
import { ItemsInformation } from '../ItemsInformation/ItemsInformation';
import printsIcon from '../MyInformationLogo/fingerprint.png';
import keyIcon from '../MyInformationLogo/key.png';
import settingsIcon from '../MyInformationLogo/settings.png';
import userIcon from '../MyInformationLogo/usercircle.png';
import './ItemsInformationContainer.scss';

/**
 * Items Information displays all cards information.
 * @returns Element.
 */
export const ItemsInformationContainer = () => {
  /**
   * My Information States
   */
  const [myGeneralInformation, setMyGeneralInformation] =
    useState<MyGeneralInformation>({
      firstName: { value: 'firstname', isEditable: false },
      name: { value: 'lastname', isEditable: false },
      mail: { value: 'mail@goo.com', isEditable: false },
      groups: {
        value: [],
        isEditable: false,
      },
    });
  const [myProfileInformation, setMyProfileInformation] =
    useState<MyProfileInformation>({
      organization: { value: 'organization', isEditable: false },
      job: { value: 'job', isEditable: false },
      use: { value: 'use', isEditable: false },
      nationality: { value: 'nationality', isEditable: false },
      country: { value: 'country', isEditable: false },
    });
  const [isUserGDH, setIsUserGDH] = useState<boolean>(true);
  const context = useContext(ConfigContext);
  const navigate = useNavigate();

  /**
   * Get processing and download quota for accessibility section.
   */
  const [downloadQuota, processingQuota] = useQueries({
    queries: [
      {
        queryKey: ['QuotasDownload', navigate, context.setError400],
        queryFn: getDownloadQuotas,
      },
      {
        queryKey: ['Quotas', navigate, context.setError400],
        queryFn: getQuotasProcess,
      },
    ],
  });

  /**
   * API service to post information profile
   * @return response
   */
  const { refetch } = useQuery({
    queryKey: [
      'EditProfile',
      {
        email: myGeneralInformation.mail['value'],
        firstName: myGeneralInformation.firstName['value'],
        lastName: myGeneralInformation.name['value'],
        attributes: {
          country: [myProfileInformation.country['value']],
          nationality: [myProfileInformation.nationality['value']],
          activity: [myProfileInformation.job['value']],
          use: [myProfileInformation.use['value']],
          organization: [myProfileInformation.organization['value']],
        },
      },
    ],
    queryFn: EditProfile,
    enabled: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    context.setUserLoggedIn({
      email: context.userLoggedIn.email,
      name:
        myGeneralInformation.firstName['value'] +
        ' ' +
        myGeneralInformation.name['value'],
      token: context.userLoggedIn.token,
    });
    // eslint-disable-next-line
  }, [myGeneralInformation]);

  /**
   * Call loadUserProfile function to get user information
   * @return MyInformation object
   */
  useEffect(() => {
    // do not run in dev env, will throw test error.
    if (process.env.NODE_ENV === 'production') {
      keycloakAdapter
        .loadUserProfile()
        .then((result: KeycloakProfile) => {
          const typedResult = toMyKeycloakProfile(result);
          const sources = ['theia', 'aeris', 'gaiadata'];
          if (typedResult && sources.includes(typedResult.attributes?.auth_source[0] as string)) {
            setIsUserGDH(false);
          }

          return keycloakAdapter.loadUserInfo().then((userInfoResult: {}) => {
            setMyGeneralInformation({
              ...myGeneralInformation,
              firstName: { value: result.firstName, isEditable: false },
              name: { value: result.lastName, isEditable: false },
              mail: { value: result.email, isEditable: false },
              groups: {
                value: (userInfoResult as KeycloakUserInfo).accessGroups,
                isEditable: false,
              },
            });

            setMyProfileInformation({
              organization: {
                value: Array.isArray(result.attributes?.organization)
                  ? result.attributes?.organization[0]
                  : '',
                isEditable: false,
              },
              job: { 
                value: Array.isArray(result.attributes?.activity) 
                  ? result.attributes?.activity[0] 
                  : '', 
                isEditable: false 
              },
              use: { 
                value: Array.isArray(result.attributes?.use) 
                  ? result.attributes?.use[0]
                  : '', 
                isEditable: false },
              nationality: {
                value: Array.isArray(result.attributes?.nationality) 
                  ? result.attributes?.nationality[0]
                  : '',
                isEditable: false,
              },
              country: {
                value: Array.isArray(result.attributes?.country)
                  ? result.attributes?.country[0]
                  : '',
                isEditable: false,
              },
            });
          });
        })
        .catch((error) => {
          console.error(error);
        });
    }
    // eslint-disable-next-line
  }, []);

  /**
   * Formats and returns accessibility quota information without using state.
   * This function directly reads the data from React Query to avoid unnecessary re-renders.
   *
   * @returns An object containing download and processing quotas.
   */
  const getAccessibilityInformation = () => {
    if (!downloadQuota.data || !processingQuota.data) {
      return {
        'download quota': { value: 'Loading...', isEditable: false },
        'processing quota': { value: 'Loading...', isEditable: false },
      };
    }

    return {
      'download quota': {
        value:
          downloadQuota.data.maxQuota === -1
            ? 'unlimited'
            : `${downloadQuota.data.remainingQuota}/${downloadQuota.data.maxQuota} (per hour)`,
        isEditable: false,
      },
      'processing quota': {
        value:
          processingQuota.data.maxQuota === -1
            ? 'unlimited'
            : `${processingQuota.data.remainingQuota}/${processingQuota.data.maxQuota} (at once)`,
        isEditable: false,
      },
    };
  };

  /**
   * My information cards
   * @return Element
   */
  return (
    <div className="itemsInformationContainer">
      <div className="cards-container">
        <div className="col">
          <div className="mb-5">
            <ItemsInformation
              isUserGDH={isUserGDH}
              informationIcon={userIcon}
              informationTitle="General"
              informationBody={myGeneralInformation}
              setMyProfileInformation={setMyProfileInformation}
              setMyGeneralInformation={setMyGeneralInformation}
              handleEditProfile={refetch}
            />
          </div>
          <div>
            <ItemsInformation
              isUserGDH={isUserGDH}
              informationIcon={printsIcon}
              informationTitle="Authentication"
              informationBody={{}}
              setMyGeneralInformation={setMyGeneralInformation}
              setMyProfileInformation={setMyProfileInformation}
              handleEditProfile={refetch}
            />
          </div>
        </div>
        <div className="col">
          <div className="mb-5">
            <ItemsInformation
              isUserGDH={isUserGDH}
              informationIcon={settingsIcon}
              informationTitle="Profile information"
              informationBody={myProfileInformation}
              setMyProfileInformation={setMyProfileInformation}
              setMyGeneralInformation={setMyGeneralInformation}
              handleEditProfile={refetch}
            />
          </div>
          <div>
            <ItemsInformation
              informationIcon={keyIcon}
              informationTitle="Accessibility"
              informationBody={getAccessibilityInformation()}
              setMyProfileInformation={setMyProfileInformation}
              setMyGeneralInformation={setMyGeneralInformation}
              handleEditProfile={refetch}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
